import { Typography, Menu, ListItemButton } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from "react";
import OZDataGrid from "../../../components/admin/common/OZDataGrid/OZDataGrid";
import OZGridToolbar from "../../../components/admin/common/OZDataGrid/OZGridToolbar";
import AssignWidgetsForm from "./AssignWidgetsForm";
import DeleteDialog from "../../../components/admin/common/CustomDeleteDialog";
import { showErrorNotification, showSuccessNotification } from "../../../components/admin/common/NotiService";
import LoadingScreen from "../../../components/admin/common/OZLoadingScreen/OZLoadingScreen";
import { getUserAssignments, deleteUserAssignmentById, getUserAssignmentById, addAndUpdateUserAssignment } from "../../../services/api-service";
import { checkRoles } from "../../../utils/util";

const AssignWidgets = forwardRef((props, ref) => {
    const [openAssignForm, setOpenAssignForm] = useState({ isNew: false, show: false });
    const [assignmentTableData, setAssignmentTableData] = useState([]);
    const [assignmentGridData, setAssignmentGridData] = useState(null);
    const [assignmentOnclickRowData, setAssignmentOnclickRowData] = useState(null);
    const [loading, setLoading] = useState(false);
    const users = useMemo(() => props.users, [props.users]);
    const widgets = useMemo(() => props.widgets, [props.widgets]);

    function EditAssignmentRowOnMouseEnter(props) {
        const [assignmentAnchorE1, setAssignmentAnchorE1] = useState(false);
        const anchorRef = useRef();
        const [openAssignmentDeleteDialog, setOpenAssignmentDeleteDialog] = useState(false);

        const onClickMoreVertIcon = () => {
            setTimeout(() => setAssignmentAnchorE1(anchorRef?.current), 1);
        };

        const closeAssignmentMorevertMenu = () => {
            setAssignmentAnchorE1(false);
        };

        function onClickDelete() {
            setAssignmentAnchorE1(false);
            setOpenAssignmentDeleteDialog(true);
        }

        const styleListItemButton = useMemo(
            () => ({
                "&.MuiButtonBase-root": {
                    color: '#212121',
                    fontFamily: "Roboto",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    height: '18px',
                    lineHeight: "14px",
                },
                '&:hover': {
                    borderRadius: "4px",
                },
            }),
            []
        );

        async function deleteAssignment() {
            const userData = users.find(user => user.name === props.data.username);
            if (!userData.id) {
                showErrorNotification("Invalid assignment ID");
                return;
            }
            setLoading(true);
            try {
                const resp = await deleteUserAssignmentById(userData.id);
                if (resp.Status === 'Fail') {
                    throw new Error(resp.Data);
                }
                if (resp.Status === "Success") {
                    getAssignmentsList();
                    showSuccessNotification("Assignment Deleted Successfully for " + props.data.username);
                }
            } catch (e) {
                showErrorNotification(e.message || "Error in Deleting Assignment");
            } finally {
                setLoading(false);
            }
            setOpenAssignmentDeleteDialog(false);
        }

        async function onClickEdit() {
            onCLickRow(props);
            setAssignmentAnchorE1(false);
        }

        return (
            <div className="oz-assignment-morevert-column-cell">
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#000000" }}>
                    {props.value || "Not Set"}
                </Typography>
                {checkRoles('ROLE_ADMIN') &&
                    <img className="delMoreIcon"
                        onClick={onClickMoreVertIcon}
                        src={`${process.env.PUBLIC_URL}/icons/more_vert.svg`} alt="edit"
                    >
                    </img>}
                <div ref={anchorRef}>
                    <Menu anchorEl={assignmentAnchorE1} open={Boolean(assignmentAnchorE1)} onClose={closeAssignmentMorevertMenu} sx={{
                        "& .MuiPaper-root": {
                            backgroundColor: "#FFFFFF",
                            width: "133px",
                            height: "62px",
                        },
                        "& .MuiMenu-paper": {
                            backgroundColor: "#FFFFFF",
                            boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.04)",
                            border: "0.0625rem solid #E6E5E6",
                            borderRadius: "12px",
                            marginLeft: "-1.0625rem",
                            marginTop: "1.2rem",
                        },
                    }}>
                        <ListItemButton
                            sx={styleListItemButton}
                            onClick={() => { onClickEdit() }}>
                            Edit
                        </ListItemButton>
                        <ListItemButton
                            onClick={onClickDelete}
                            sx={{ ...styleListItemButton, marginTop: '7px' }}>
                            Delete
                        </ListItemButton>
                    </Menu>
                    {openAssignmentDeleteDialog && <DeleteDialog
                        open={openAssignmentDeleteDialog}
                        onClose={() => { setOpenAssignmentDeleteDialog(false) }}
                        title="Delete Assignment"
                        content={`This will delete the widget assignment for ${props.data.userName} permanently. You cannot undo this action.`}
                        deleteFunction={deleteAssignment} />}
                </div>
            </div>
        );
    };

    function AssignedWidgetsCellRenderer(params) {
        const gridItemStyle = useMemo(
            () => ({
                fontWeight: 400,
                fontSize: "12px",
                color: "#000000"
            }),
            []
        );

        try {
            const widgets = Array.isArray(params.value) ? params.value : [];
            return <Typography style={{ ...gridItemStyle }}>{widgets.join(', ')}</Typography>;
        } catch (e) {
            return <Typography style={{ ...gridItemStyle }}>No widgets assigned</Typography>;
        }
    }

    async function getAssignmentsList() {
        setLoading(true);
        try {
            const resp = await getUserAssignments();
            if (resp.Status === "Success" && resp.Data && Array.isArray(resp.Data)) {
                setAssignmentTableData(resp.Data)
            } else {
                throw new Error(resp.Message);
            }
        } catch (e) {
            console.log("Error in Assignments list", e);
            setAssignmentTableData([]);
        } finally {
            setLoading(false);
        }
    }

    async function addAssignment(formData, closeAssignForm, type) {
        setLoading(true)
        try {
            const resp = await addAndUpdateUserAssignment(formData);

            if (resp.Status === "Success") {
                getAssignmentsList();
                const userName = users.find(user => user.id === formData.userId)?.name;
                showSuccessNotification(`Assignment ${type === "create" ? "Created" : "Updated"} Successfully for ${userName}`);
                closeAssignForm();
            }
        } catch (e) {
            console.log("Error in Creating/Updating Assignment", e);
            showErrorNotification(e?.message || `Error in ${type === "create" ? "Creating" : "Updating"} Assignment`);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getAssignmentsList();
    }, []);

    const columnDefs = [
        {
            field: "username",
            headerName: "User Name",
        },
        {
            field: "widgetNames",
            headerName: "Assigned Widgets",
            cellRenderer: AssignedWidgetsCellRenderer,
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            cellRenderer: EditAssignmentRowOnMouseEnter,
        },
        {
            field: "updatedDate",
            headerName: "Updated Date",
            cellRenderer: EditAssignmentRowOnMouseEnter,
        },
    ];



    const showAssignForm = () => {
        setOpenAssignForm({ isNew: true, show: true });
    };

    useImperativeHandle(ref, () => ({
        showAssignForm
    }));

    const closeAssignForm = () => {
        setOpenAssignForm(false);
        setOpenAssignForm({ isNew: false, show: false });
    };

    async function onCLickRow(rowItem) {
        setOpenAssignForm({ isNew: false, show: true });
        setAssignmentOnclickRowData({});
        try {
            const userData = users.find(user => user.name === rowItem.data.username);
            const resp = await getUserAssignmentById(userData.id);
            if (resp.Status === "Success" && resp.Data) {
                const assignmentData = resp.Data;
                setAssignmentOnclickRowData({
                    selectedUser: userData || null,
                    userId: userData.id,
                    assignedWidgets: assignmentData.widgets || [],
                    id: assignmentData?.id || null
                });
            } else {
                throw new Error(resp.Message);
            }
        } catch (e) {
            console.log("Error in assignment details", e);
            showErrorNotification(e?.message || "Error in assignment details");
        }
    }

    return (
        <div className="oz-assignment-container">
            {openAssignForm.show && openAssignForm.isNew && (
                <AssignWidgetsForm
                    updateAssignments={getAssignmentsList}
                    onClose={closeAssignForm}
                    addAssignment={addAssignment}
                    users={users}
                    widgets={widgets}
                />
            )}
            {openAssignForm.show && !openAssignForm.isNew && (
                <AssignWidgetsForm
                    updateAssignments={getAssignmentsList}
                    onClose={closeAssignForm}
                    data={assignmentOnclickRowData}
                    openDialog={true}
                    editAccess={checkRoles('ROLE_ADMIN')}
                    addAssignment={addAssignment}
                    users={users}
                    widgets={widgets}
                />
            )}

            <OZGridToolbar
                showImport={false}
                refreshGrid={getAssignmentsList}
                title='List of Widget Assignments'
                searchPlaceholder='User Name'
                rowCount={assignmentTableData.length}
                dataGridRef={assignmentGridData}
                fileName='widgetAssignments'
            />
            {loading ?
                (
                    <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: '0,0,0,0.5', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} >
                        <LoadingScreen />
                    </div>
                ) :
                (<>
                    <OZDataGrid
                        height={"26.3rem"}
                        columns={columnDefs}
                        data={assignmentTableData}
                        setGridRef={setAssignmentGridData}
                        rowClickCallback={onCLickRow}
                    />
                </>
                )
            }
        </div>
    );
});

export default AssignWidgets;